/**
 * SearchBar Component
 *
 * Reusable search input with debounce for admin pages
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-299
 */

import { useState, useMemo, useEffect } from 'react';
import { Search, X } from 'lucide-react';

/**
 * Debounce function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default function SearchBar({ onSearch, placeholder = "Search...", initialValue = '' }) {
  const [value, setValue] = useState(initialValue);

  // Create debounced search function (300ms delay)
  const debouncedSearch = useMemo(
    () => debounce(onSearch, 300),
    [onSearch]
  );

  /**
   * Handle input change
   */
  const handleChange = (  _e) => {
    const newValue = _e.target.value;
    setValue(newValue);
    debouncedSearch(newValue);
  };

  /**
   * Handle clear button
   */
  const handleClear = () => {
    setValue('');
    onSearch('');
  };

  /**
   * Update value if initialValue changes
   */
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
      />
      {value && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
